import { useState, type FormEvent } from 'react'
import type { VisitRow } from '@fitting-room/contracts'
import { useAppointmentTypes, useBackOffice, useRescheduleAppointment } from '../../lib/adminQueries'
import { formatMinutes } from '../../lib/appointments'
import { boutiqueDateTime, boutiqueDayOf, boutiqueWallTime } from '../../lib/boutique'
import { useStaffToken } from '../../lib/staffToken'
import { DatePicker } from './DatePicker'
import { FormField, FormSheet } from './FormSheet'
import { TimeSelect } from './TimeSelect'

/** The lengths offered beside "usual". An appointment holding another keeps it on the list. */
export const LENGTH_CHOICES: readonly number[] = [30, 45, 60, 75, 90, 120, 150, 180]

/**
 * Only "when": the date, the time, how long. Ported from tperent's
 * `RescheduleDialog`, which settled the split — the customer's details are
 * edited elsewhere and the status has its own buttons, so moving an
 * appointment can never change anything else about it by accident.
 *
 * It starts from the appointment's current time. Nine moves in ten change only
 * the day, and a blank form would make the stylist find the time again — the
 * one fact that vanishes from the screen the moment the sheet opens.
 */
export function RescheduleDialog({ visit, onClose }: { readonly visit: VisitRow | null; readonly onClose: () => void }) {
  return (
    <FormSheet
      open={visit !== null}
      onOpenChange={(next) => (next ? undefined : onClose())}
      title="Reschedule"
      description="Their fitting room stays open until the new time, and their reminders move with it."
    >
      {visit ? <RescheduleForm key={visit.id} visit={visit} onClose={onClose} /> : null}
    </FormSheet>
  )
}

function RescheduleForm({ visit, onClose }: { readonly visit: VisitRow; readonly onClose: () => void }) {
  const { token } = useStaffToken()
  const types = useAppointmentTypes(token).data?.types ?? []
  const presets = useBackOffice(token).data?.appointmentTimes ?? []
  const move = useRescheduleAppointment(token, visit.id)
  const [date, setDate] = useState(boutiqueDayOf(visit.scheduledAt))
  const [time, setTime] = useState(boutiqueWallTime(visit.scheduledAt))
  const [length, setLength] = useState(visit.durationMinutes ? String(visit.durationMinutes) : '')
  const [error, setError] = useState<string | null>(null)

  const usual = types.find((t) => t.key === visit.tierKey)?.durationMinutes
  const choices = visit.durationMinutes && !LENGTH_CHOICES.includes(visit.durationMinutes) ? [...LENGTH_CHOICES, visit.durationMinutes].sort((a, b) => a - b) : LENGTH_CHOICES

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!date || !time) return setError('Pick a date and a time.')
    setError(null)
    move.mutate({ date, time, durationMinutes: length ? Number(length) : null }, { onSuccess: onClose })
  }

  return (
    <form onSubmit={submit} noValidate>
      <p className="st-meta mb-4 text-sm">Now: {boutiqueDateTime(visit.scheduledAt)}</p>
      <div className="mb-3 flex flex-col gap-1">
        <span className="st-eyebrow">Date</span>
        <div>
          <DatePicker value={date} onChange={setDate} label="New date" />
        </div>
      </div>
      <FormField label="Time">
        <TimeSelect value={time} onChange={setTime} presets={presets} />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Length">
          <select className="st-select" value={length} onChange={(event) => setLength(event.target.value)}>
            <option value="">{usual ? `Usual (${formatMinutes(usual)})` : 'Usual'}</option>
            {choices.map((minutes) => (
              <option key={minutes} value={minutes}>
                {formatMinutes(minutes)}
              </option>
            ))}
          </select>
        </FormField>
      </div>

      {error ? (
        <p className="st-alert mb-3" role="alert">
          {error}
        </p>
      ) : null}
      {move.isError ? (
        <p className="st-alert mb-3" role="alert">
          {move.error.message}
        </p>
      ) : null}

      <div className="mt-5 flex gap-2">
        <button type="button" className="st-btn-ghost flex-1" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="st-btn flex-[2]" disabled={move.isPending}>
          {move.isPending ? 'Moving…' : 'Move appointment'}
        </button>
      </div>
    </form>
  )
}
